import React from 'react';
import styled from 'styled-components';
import { Body5 } from '@ui';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';

const Wrapper = styled.div`
  position: relative;
`;

const TriggerWrapper = styled.div`
  button {
    padding: 6px 12px;
  }
`;

const MenuWrapper = styled.div`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  min-width: 240px;
  max-height: 320px;
  overflow-y: auto;

  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.appBackground};
  box-shadow: 0 2px 6px 1px rgba(0, 0, 0, 0.12);
  padding: 8px 0;
  z-index: 10000;
`;

const OptionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  padding: 8px 16px;
  cursor: pointer;

  background: ${({ theme, $isSelected }) => ($isSelected ? theme.themeColors.lowContrastBorder : 'transparent')};

  &:hover {
    background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  }
`;

const OptionLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CheckMark = styled.span`
  width: 12px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.themeColors.primaryCtaButton};
`;

export const FacetDropdown = ({ label, options, selectedValues, onToggleValue, onClear }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [coords, setCoords] = React.useState({ top: 0, left: 0 });
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const selectedCount = selectedValues?.length || 0;

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.left });
    setIsOpen(true);
  };

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handleMouseDown = (event) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!inTrigger && !inMenu) setIsOpen(false);
    };
    const handleScroll = () => setIsOpen(false);
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  if (!options || options.length === 0) return null;

  return (
    <Wrapper>
      <TriggerWrapper ref={triggerRef}>
        <SkeletonButton onClick={handleToggle}>
          {selectedCount > 0 ? `${label} (${selectedCount})` : label}
        </SkeletonButton>
      </TriggerWrapper>
      {isOpen ? (
        <MenuWrapper ref={menuRef} $top={coords.top} $left={coords.left}>
          {selectedCount > 0 ? (
            <OptionRow onClick={onClear}>
              <Body5 isLowContrast>Clear {label.toLowerCase()}</Body5>
            </OptionRow>
          ) : null}
          {options.map((option) => {
            const isSelected = selectedValues?.includes(option.value);
            return (
              <OptionRow key={option.value} $isSelected={isSelected} onClick={() => onToggleValue(option.value)}>
                <OptionLabelWrapper>
                  <CheckMark>{isSelected ? '✓' : ''}</CheckMark>
                  <Body5 truncate>{option.label}</Body5>
                </OptionLabelWrapper>
                <Body5 isLowContrast>{option.count}</Body5>
              </OptionRow>
            );
          })}
        </MenuWrapper>
      ) : null}
    </Wrapper>
  );
};
